import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CodingAPI, type Matrix, type ProposedMatrixResponse } from '../../api/coding';
import { ErrorBanner, Spinner } from '../Layout';

interface GenerateMatrixModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  surveyId?: number;
  initialQuestion?: string;
  variableName?: string;
  initialSamples?: string[];
  onMatrixGenerated: (name: string, matrix: Matrix, usageInfo?: ProposedMatrixResponse['usage']) => void;
}

export default function GenerateMatrixModal({
  isOpen,
  onClose,
  projectId,
  surveyId,
  initialQuestion = '',
  variableName = '',
  initialSamples = [],
  onMatrixGenerated,
}: GenerateMatrixModalProps) {
  const [question, setQuestion] = useState(initialQuestion);
  const [instructions, setInstructions] = useState('');
  const [nCategories, setNCategories] = useState(8);
  const [useRealSamples, setUseRealSamples] = useState(true);
  const [samplesText, setSamplesText] = useState('');
  const [isAdjustingPrompt, setIsAdjustingPrompt] = useState(false);
  const [customSystemPrompt, setCustomSystemPrompt] = useState('');
  const [customUserPrompt, setCustomUserPrompt] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Obtener plantilla y estado del prompt desde el backend
  const { data: promptInfo, isLoading: loadingPrompt } = useQuery({
    queryKey: ['matrix-prompt', projectId],
    queryFn: () => CodingAPI.getMatrixPrompt(projectId),
    enabled: isOpen,
    staleTime: 60_000,
  });

  // Si hay survey y variable pero no samples iniciales, cargar respuestas reales de ODK
  const { data: fetchedSamples, isLoading: loadingSamples } = useQuery({
    queryKey: ['coding-samples-modal', surveyId, variableName],
    queryFn: () => CodingAPI.sampleResponses(surveyId!, variableName!, 30),
    enabled: isOpen && Boolean(surveyId && variableName && initialSamples.length === 0),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (initialQuestion) setQuestion(initialQuestion);
  }, [initialQuestion]);

  useEffect(() => {
    if (initialSamples && initialSamples.length > 0) {
      setSamplesText(initialSamples.join('\n'));
    } else if (fetchedSamples?.items && fetchedSamples.items.length > 0) {
      setSamplesText(fetchedSamples.items.map((i) => i.text).join('\n'));
    }
  }, [initialSamples, fetchedSamples]);

  useEffect(() => {
    if (promptInfo?.vigente) {
      setCustomSystemPrompt(promptInfo.vigente.sistema);
      setCustomUserPrompt(promptInfo.vigente.usuario);
    }
  }, [promptInfo]);

  if (!isOpen) return null;

  const realSamplesList = samplesText
    .split('\n')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  const isPromptCustomized =
    Boolean(promptInfo?.vigente) &&
    (customSystemPrompt !== promptInfo?.vigente.sistema || customUserPrompt !== promptInfo?.vigente.usuario);

  const handleResetPrompts = () => {
    if (promptInfo?.vigente) {
      setCustomSystemPrompt(promptInfo.vigente.sistema);
      setCustomUserPrompt(promptInfo.vigente.usuario);
    }
  };

  const handleGenerate = async () => {
    if (!question.trim()) {
      setError('Por favor ingrese el texto de la pregunta a codificar.');
      return;
    }
    setError(null);
    setIsBusy(true);

    try {
      const payload = {
        project_id: projectId,
        question: question.trim(),
        sample_responses: useRealSamples ? realSamplesList : [],
        instructions: instructions.trim(),
        n_categories: nCategories,
        custom_system_prompt: isPromptCustomized ? customSystemPrompt : undefined,
        custom_user_prompt: isPromptCustomized ? customUserPrompt : undefined,
        survey_id: surveyId,
        variable_name: variableName || undefined,
      };

      const result = await CodingAPI.proposeMatrix(payload);
      onMatrixGenerated(result.name, result.specification, result.usage);
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg || 'Ocurrió un fallo al comunicarse con el servicio de IA.');
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-tinta/60 backdrop-blur-xs p-3 sm:p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-matrix-ia-title"
    >
      <div className="relative bg-fondo border border-grid rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col my-auto overflow-hidden">
        {/* Encabezado */}
        <div className="px-5 py-4 border-b border-grid flex items-start justify-between gap-3 bg-plano">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 id="modal-matrix-ia-title" className="text-lg font-bold text-tinta flex items-center gap-2">
                <span>🪄</span> Generar matriz de codificación con IA
              </h2>
              {promptInfo && (
                <span
                  className={`text-[11px] font-mono px-2 py-0.5 rounded border ${
                    promptInfo.ia_configurada
                      ? 'bg-estado-exito/10 text-estado-exito border-estado-exito/30'
                      : 'bg-estado-aviso/10 text-estado-aviso border-estado-aviso/30'
                  }`}
                >
                  {promptInfo.proveedor} · {promptInfo.modelo || 'modelo no asignado'}
                </span>
              )}
            </div>
            <p className="text-xs text-tinta-3 mt-1">
              Diseña un catálogo riguroso con categorías mutuamente excluyentes, definiciones, inclusiones, exclusiones
              y citas reales de la encuesta.
            </p>
          </div>
          <button
            type="button"
            className="text-tinta-3 hover:text-tinta p-2 rounded-md hover:bg-superficie text-lg leading-none"
            onClick={onClose}
            aria-label="Cerrar modal"
            disabled={isBusy}
          >
            ✕
          </button>
        </div>

        {/* Contenido scrolleable */}
        <div className="p-5 space-y-4 overflow-y-auto min-w-0 flex-1">
          {error && <ErrorBanner message={error} />}

          {promptInfo && !promptInfo.ia_configurada && (
            <div className="p-3 bg-estado-aviso/15 border border-estado-aviso/40 rounded text-xs text-tinta-2 space-y-1">
              <p className="font-semibold text-tinta">⚠️ La inteligencia artificial aún no está configurada</p>
              <p>
                Debe registrar el proveedor y clave de API en el panel de control para que el modelo pueda procesar sus
                preguntas.
              </p>
              <a
                href="/admin/configuracion"
                target="_blank"
                rel="noreferrer"
                className="text-marca underline font-medium inline-block mt-1"
              >
                Abrir Configuración → Inteligencia artificial ↗
              </a>
            </div>
          )}

          {/* Pregunta a codificar */}
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <label className="etiqueta mb-0">
                Pregunta del formulario u objetivo a codificar: <span className="text-estado-critico">*</span>
              </label>
              {variableName && (
                <span className="text-[11px] font-mono bg-plano px-2 py-0.5 rounded border border-grid text-tinta-2">
                  Var: {variableName}
                </span>
              )}
            </div>
            <textarea
              className="input text-sm min-h-[70px]"
              rows={2}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ej. ¿Cuál es el principal problema que afecta a su familia en este momento?"
              disabled={isBusy}
              required
            />
          </div>

          {/* Enfoque e instrucciones específicas */}
          <div className="space-y-1">
            <label className="etiqueta mb-0">
              Enfoque o instrucciones particulares para la categorización (opcional):
            </label>
            <input
              className="input text-sm min-h-[44px]"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Ej. Enfocar en servicios públicos, separar agua de luz, y priorizar motivos de queja vecinal"
              disabled={isBusy}
            />
            <p className="text-[11px] text-tinta-3">
              Guía al modelo sobre el nivel de detalle o distinciones críticas requeridas para esta pregunta.
            </p>
          </div>

          {/* Número orientativo de categorías */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center bg-plano p-3 rounded border border-grid">
            <div>
              <label className="etiqueta mb-0">Número orientativo de categorías:</label>
              <p className="text-[11px] text-tinta-3">
                Rango recomendado entre 5 y 12 temas principales más estados de respuesta estándar.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="4"
                max="16"
                step="1"
                value={nCategories}
                onChange={(e) => setNCategories(Number(e.target.value))}
                className="flex-1 accent-marca cursor-pointer min-h-[44px]"
                disabled={isBusy}
              />
              <span className="text-base font-bold font-mono px-3 py-1 bg-fondo border border-grid rounded text-tinta min-w-[44px] text-center">
                {nCategories}
              </span>
            </div>
          </div>

          {/* Muestra de respuestas reales */}
          <div className="border border-grid rounded-lg p-3 space-y-2 bg-superficie">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="flex items-center gap-2 text-sm font-medium text-tinta cursor-pointer">
                <input
                  type="checkbox"
                  checked={useRealSamples}
                  onChange={(e) => setUseRealSamples(e.target.checked)}
                  disabled={isBusy}
                  className="rounded border-grid min-w-[18px] min-h-[18px] accent-marca"
                />
                Utilizar muestra de respuestas reales de campo ({realSamplesList.length} disponibles)
              </label>
              {loadingSamples && <span className="text-xs text-tinta-3 flex items-center gap-1">Cargando ODK…</span>}
            </div>

            {useRealSamples && (
              <div className="space-y-1 pt-1">
                <p className="text-xs text-tinta-3">
                  Una respuesta por línea. El modelo usará estas citas textuales reales para calibrar las categorías y
                  extraer ejemplos auténticos.
                </p>
                <textarea
                  className="input font-mono text-xs min-h-[90px]"
                  rows={4}
                  value={samplesText}
                  onChange={(e) => setSamplesText(e.target.value)}
                  placeholder="falta de agua potable&#10;inseguridad y robos por las noches&#10;las pistas están rotas&#10;no hay alumbrado público"
                  disabled={isBusy}
                />
              </div>
            )}
          </div>

          {/* Sección de ajuste de prompts (Avanzado / Configurable) */}
          <div className="border border-grid rounded-lg overflow-hidden bg-plano">
            <button
              type="button"
              className="w-full px-4 py-3 text-left font-medium text-sm text-tinta flex items-center justify-between hover:bg-superficie transition-colors"
              onClick={() => setIsAdjustingPrompt(!isAdjustingPrompt)}
            >
              <span className="flex items-center gap-2">
                <span>⚙️</span> Ajustar prompts del modelo antes de generar
                {isPromptCustomized && (
                  <span className="text-[10px] bg-marca/10 text-marca border border-marca/30 px-1.5 py-0.5 rounded font-bold">
                    personalizado para esta llamada
                  </span>
                )}
              </span>
              <span className="text-xs text-tinta-3">{isAdjustingPrompt ? '▲ Ocultar' : '▼ Ver y editar'}</span>
            </button>

            {isAdjustingPrompt && (
              <div className="p-4 border-t border-grid space-y-3 bg-fondo text-xs">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-tinta-3">
                    Ajuste el papel del modelo y la plantilla de datos para esta solicitud. Para guardar un prompt
                    permanente para todas las encuestas, visite la sección de Prompts.
                  </p>
                  <a
                    href="/admin/prompts"
                    target="_blank"
                    rel="noreferrer"
                    className="text-marca hover:underline font-medium flex items-center gap-1"
                  >
                    Abrir Configuración → Prompts ↗
                  </a>
                </div>

                {/* Prompt de sistema */}
                <div className="space-y-1">
                  <label className="etiqueta mb-0 text-xs">Prompt de sistema (Reglas y formato de salida):</label>
                  <textarea
                    className="input font-mono text-xs leading-relaxed"
                    rows={4}
                    value={customSystemPrompt}
                    onChange={(e) => setCustomSystemPrompt(e.target.value)}
                    disabled={isBusy}
                  />
                </div>

                {/* Prompt de usuario */}
                <div className="space-y-1">
                  <label className="etiqueta mb-0 text-xs">
                    Prompt de usuario (Variables disponibles:{' '}
                    <span className="font-mono text-marca">
                      {promptInfo?.variables.join(' ') || '{pregunta} {respuestas} {instrucciones} {n_categorias} {esquema}'}
                    </span>
                    ):
                  </label>
                  <textarea
                    className="input font-mono text-xs leading-relaxed"
                    rows={5}
                    value={customUserPrompt}
                    onChange={(e) => setCustomUserPrompt(e.target.value)}
                    disabled={isBusy}
                  />
                </div>

                {isPromptCustomized && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      className="btn-sec text-xs min-h-[36px]"
                      onClick={handleResetPrompts}
                      disabled={isBusy}
                    >
                      Restablecer prompts predeterminados
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Pie del modal / Botones de acción */}
        <div className="px-5 py-3 border-t border-grid bg-plano flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-tinta-3">
            {isBusy ? (
              <span className="flex items-center gap-2 text-marca font-medium">
                <Spinner /> Generando matriz con {promptInfo?.proveedor || 'IA'}…
              </span>
            ) : (
              <span>El borrador generado se cargará en el editor para su revisión humana previa.</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button type="button" className="btn-sec text-xs min-h-[44px]" onClick={onClose} disabled={isBusy}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn text-xs min-h-[44px] px-4 font-semibold"
              onClick={handleGenerate}
              disabled={isBusy || loadingPrompt || !question.trim() || !promptInfo?.ia_configurada}
            >
              {isBusy ? 'Generando…' : '🪄 Generar matriz con IA'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
